import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    inject
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapSelectComponent } from "@components/map/components/menu/map-select/map-select.component";
import { MapComponent } from "@components/map/map.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { remove, uniq } from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LayerVisibility } from "~app/common/components/map/components/widgets/layer-list/models/layer-visibility.model";
import { MapDeselectedEvent, MapHoverEvent, MapSelectedEvent } from "~app/common/components/map/map.events";
import { IMapComponent } from "~app/common/components/map/map.models";
import {
    IFilterParams,
    IMapDrawLayer,
    IMapGeometry,
    IMapSettings,
    IRenderParams
} from "~app/common/components/map/map.settings";
import { IMapQueryResult } from "~app/common/components/map/services/map-query.service";
import { MottagningsplatsMarkerDTO } from "~app/common/models/mottagningsplats/MottagningsplatsMarkerDTO";
import { TplTransportforetagDTO } from "~app/common/models/transport/TplTransportforetagDTO";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { TransportforetagService } from "~app/features/transport/transportforetag/services/transportforetag.service";
import { ArbetsorderListHoverInMessage, ArbetsorderListHoverOutMessage, ArbetsorderMapHoverInMessage, ArbetsorderMapHoverOutMessage } from "../../events/ArbetsorderHoverMessage";
import { TplFilterApplied, TplFilterReset } from "../../events/tplEventHandler";
import { ITplArbetsorder } from "../../models/dtos";
import { TplService } from "../../services/tpl.service";
import { ITplState, ITplTransportsatt, TplTabId } from "../../state/state-models";
import { TplStateService } from "../../state/tpl-state.service";
import { ICallbackResult } from "../tpl-arbetsorder-info-modal/tpl-arbetsorder-info-modal-models";
import { TplArbetsorderInfoModalComponent } from "../tpl-arbetsorder-info-modal/tpl-arbetsorder-info-modal.component";
import { TplLegendComponent } from "../tpl-legend/tpl-legend.component";
import { TplMottagningsplatsModalComponent } from "../tpl-mottagningsplats-modal/tpl-mottagningsplats-modal.component";
import { TplVolymComponent } from "../tpl-volym/tpl-volym.component";
import { TplLayerBinder } from "./tpl-layer-binder";

@Component({
    selector: "rask-tpl-karta",
    templateUrl: "./tpl-karta.component.html",
    styleUrls: ["./tpl-karta.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapComponent,
        MapSelectComponent,
        MapLayersComponent,
        TplVolymComponent,
        TplLegendComponent,
        RouterLink
    ]
})
export class TplKartaComponent implements OnInit, OnDestroy {
    ao: number[];
    aoSelected: number[];
    clickedItem = {
        visible: false,
        label: "",
        route: null,
        id: null
    };
    filter = new Subject<IFilterParams>();
    filteredDestineringsomraden: number[];
    legendData = [];
    mapsettings: IMapSettings;
    @ViewChild("popup", { static: true }) popup: ElementRef;
    refresh = new Subject<IRenderParams>();
    state: ITplState;

    private readonly aoDestineraLayerId = "arbetsordrar-omdestinera";
    private readonly aoNeutralLayerId = "arbetsordrar-neutral";
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly colorAoDefault = "white";
    private readonly colorAoSelected = "cyan";
    private readonly destineringsomradenId = "Destineringsomraden";
    private readonly eventService = inject(EventService);
    private readonly modal = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly renderer = inject(Renderer2);
    private readonly selectedDestineringsomraden: IMapGeometry[] = [];
    private readonly stateService = inject(TplStateService);
    private readonly tplService = inject(TplService);
    private readonly transportforetagService = inject(TransportforetagService);

    private changingState: number;
    private map: IMapComponent;
    private mottagningsplatsIcons: MottagningsplatsMarkerDTO[];
    private targetState: string;
    private tplLayerBinder: TplLayerBinder;
    private transportForetag: TplTransportforetagDTO[];
    private transportsatt: ITplTransportsatt = {
        rundvirke: true,
        bransle: false
    };

    aoHoverIn(geometry: IMapGeometry, mouseevent: any) {
        this.hoverIn(geometry, mouseevent);
        if (geometry) {
            this.eventService.emit(new ArbetsorderMapHoverInMessage(geometry.id));
        }
    }

    aoHoverOut(geometry: IMapGeometry, mouseevent: any) {
        this.hoverOut(geometry, mouseevent);
        this.eventService.emit(new ArbetsorderMapHoverOutMessage(geometry.id));
    }

    arbetsordrarClick(geometry: IMapGeometry, mouseevent: any, isCtrl: boolean) {
        if (geometry && isCtrl === false) {
            const subset = this.map?.getSubset();
            if (subset === "omdestinera" || subset === "transporttilldelning" || this.targetState === TplTabId.hanteraao) {
                TplArbetsorderInfoModalComponent.open({
                    arbetsorderId: geometry.id,
                    showSelected: true,
                    isSelected: geometry.selected
                }, this.modal, (result) => {
                    this.handleTplArbetsorderInfoModalResult(geometry, result);
                });
            }
        }
    }

    arbetsordrarPan(geometries: IMapGeometry[]) {
        this.legendData = geometries;
    }

    clickDestineringsomrade(geometry: IMapGeometry, isCtrl = false) {
        if (ObjectUtils.isNullOrUndefined(geometry)) {
            return;
        }
        this.updateSelectedDestineringsomrade(geometry, isCtrl);
    }

    clickDestineringsomradeFeature(queryResult: IMapQueryResult[], isCtrl = false) {
        if (queryResult?.length > 0 && queryResult[0].geometries?.length > 0) {
            const filteredGeometries = this.filteredDestineringsomraden?.length > 0 ?
                queryResult[0].geometries.filter(g => this.filteredDestineringsomraden.some(dsoId => dsoId === g.attributes.Id)) :
                queryResult[0].geometries;
            if (filteredGeometries.length === 0) {
                return;
            }
            const omrade = filteredGeometries[0];
            omrade.visible = true;
            omrade.styleName = "selectedPolygon";
            omrade.id = omrade.attributes?.Id ?? omrade.geoid;
            this.updateSelectedDestineringsomrade(omrade, isCtrl);
        }
    }

    hideTooltip() {
        this.clickedItem.visible = false;
        this.triggerDigestCycle();
    }

    hoverIn(geometry: IMapGeometry, mouseevent: any) {
        if (geometry) {
            this.showTooltip(geometry?.label, mouseevent.x, mouseevent.y);
        }
    }

    hoverOut(geometry: IMapGeometry, mouseevent: any) {
        this.hideTooltip();
    }

    initMapSettings() {
        this.mapsettings = {
            id: "transportplanerakarta",
            height: "100%",
            center: [
                17.9531,
                59.3565
            ],
            zoom: 0,
            coords: true,
            zoomButtons: true,
            onLayerReady: (map, layer) => this.layerReady(map, layer),
            onMapReady: (map) => this.mapReady(map),
            onMapPan: (map) => this.mapPan(map),
            onMapClick: (mouse, hitlist) => this.onMapClick(mouse, hitlist),
            baselayers: [
                {
                    id: "metriabakgrund",
                    visible: true
                }
            ],
            featurelayers: [
                {
                    id: "VerkGemAdminGrans",
                    visible: true
                },
                {
                    id: "VerkGemLLD",
                    visible: true
                },
                {
                    id: "VerkGemLKF",
                    visible: true
                },
                {
                    id: "VerkGemFastigheter",
                    visible: true
                },
                {
                    id: "VerkGemSamfalligheter",
                    visible: true
                },
                {
                    id: "VerkGemFaktaExternVindkraft",
                    visible: true
                },
                {
                    id: "VerkGemFaktaBelastningar",
                    visible: true
                },
                {
                    id: "Vagar",
                    visible: true
                },
                {
                    id: "VFS_PlaneradVag",
                    visible: true
                },
                {
                    id: "Transportomraden",
                    visible: true
                },
                {
                    id: this.destineringsomradenId,
                    visible: true,
                    onClickFeature: (geometries, mouse, isCtrl) => {
                        this.clickDestineringsomradeFeature(geometries, isCtrl);
                        return false;
                    }
                },
                {
                    id: "Satellitbilder",
                    visible: true
                },
                {
                    id: "Drone",
                    visible: true
                },
                {
                    id: "smittad_zon_sjv",
                    visible: true
                }
            ],
            drawLayers: [
                {
                    id: "mottagningsplatsicons",
                    visible: true,
                    readonly: true,
                    title: "Mottagningsplatser",
                    geometries: [],
                    color: [
                        128,
                        128,
                        128,
                        1
                    ],
                    onClick: (mouseevent, geometry) => this.mottagningsplatsIconClick(geometry, mouseevent),
                    onHoverIn: (geom: IMapGeometry, mouseevent: any) => this.hoverIn(geom, mouseevent),
                    onHoverOut: (geom: IMapGeometry, mouseevent: any) => this.hoverOut(geom, mouseevent)
                } as IMapDrawLayer,
                {
                    id: "transportforetagicons",
                    visible: true,
                    readonly: true,
                    title: "Transportföretag",
                    geometries: [],
                    color: [
                        0,
                        0,
                        0,
                        1
                    ],
                    onClick: (mouseevent, geometry) => this.transportforetagIconClick(geometry, mouseevent),
                    onHoverIn: (geom: IMapGeometry, mouseevent: any) => this.hoverIn(geom, mouseevent),
                    onHoverOut: (geom: IMapGeometry, mouseevent: any) => this.hoverOut(geom, mouseevent)
                } as IMapDrawLayer,
                {
                    id: "destinationer",
                    title: "Destineringar",
                    visible: true,
                    readonly: true,
                    geometries: []
                },
                {
                    id: "arbetsordrar",
                    title: "Arbetsordrar",
                    readonly: true,
                    selectable: true,
                    color: this.colorAoDefault,
                    colorSelected: this.colorAoSelected,
                    visible: true,
                    autoZoom: true,
                    zoomable: true,
                    geometries: [],
                    hideInLayerList: true,
                    onClick: (mouseevent, geometry, isCtrl) => {
                        this.arbetsordrarClick(geometry, mouseevent, isCtrl);
                        return false;
                    },
                    onPan: (geometries: IMapGeometry[]) => this.arbetsordrarPan(geometries),
                    onHoverIn: (geometry, mouseevent) => this.aoHoverIn(geometry, mouseevent),
                    onHoverOut: (geometry, mouseevent) => this.aoHoverOut(geometry, mouseevent)
                } as IMapDrawLayer,
                {
                    id: this.aoDestineraLayerId,
                    title: "Arbetsordrar",
                    color: "white",
                    readonly: true,
                    selectable: true,
                    visible: true,
                    autoZoom: true,
                    zoomable: true,
                    geometries: [],
                    hideInLayerList: true,
                    onClick: (mouseevent, geometry, isCtrl) => {
                        this.arbetsordrarClick(geometry, mouseevent, isCtrl);
                        return false;
                    },
                    onHoverIn: (geometry, mouseevent) => this.aoHoverIn(geometry, mouseevent),
                    onHoverOut: (geometry, mouseevent) => this.aoHoverOut(geometry, mouseevent)
                } as IMapDrawLayer,
                {
                    id: this.aoNeutralLayerId,
                    title: "Arbetsordrar",
                    color: "white",
                    readonly: true,
                    selectable: true,
                    visible: true,
                    autoZoom: true,
                    zoomable: true,
                    geometries: [],
                    hideInLayerList: true,
                    onClick: (mouseevent, geometry, isCtrl) => {
                        this.arbetsordrarClick(geometry, mouseevent, isCtrl);
                        return false;
                    },
                    onHoverIn: (geometry, mouseevent) => this.aoHoverIn(geometry, mouseevent),
                    onHoverOut: (geometry, mouseevent) => this.aoHoverOut(geometry, mouseevent)
                } as IMapDrawLayer,
                {
                    id: "destineringsomraden_select",
                    title: "Destineringsområden selekterade",
                    readonly: true,
                    selectable: false,
                    visible: true,
                    hideInLayerList: true,
                    geometries: [],
                    onClick: (mouseevent, geometry, isCtrl) => {
                        this.clickDestineringsomrade(geometry, isCtrl);
                        return false;
                    }
                }
            ],
            subset: [
                {
                    id: "neutral",
                    layerIds: [

                        "Vagar",
                        "VFS_PlaneradVag",
                        "Satellitbilder",
                        "Drone",
                        "smittad_zon_sjv",
                        "VerkGemFaktaExternVindkraft",
                        "VerkGemFaktaBelastningar",
                        this.aoNeutralLayerId
                    ]
                },

                {
                    id: "transporttilldelning",
                    layerIds: [
                        "VerkGemAdminGrans",
                        "VerkGemLLD",
                        "VerkGemLKF",
                        "VerkGemFastigheter",
                        "Transportomraden",
                        "Vagar",
                        "VFS_PlaneradVag",
                        "Satellitbilder",
                        "Drone",
                        "smittad_zon_sjv",
                        "VerkGemFaktaExternVindkraft",
                        "VerkGemFaktaBelastningar",
                        "VerkGemSamfalligheter",
                        "arbetsordrar",
                        "transportforetagicons"
                    ]
                },
                {
                    id: "omdestinera",
                    layerIds: [
                        "VerkGemAdminGrans",
                        "VerkGemSamfalligheter",
                        "VerkGemLLD",
                        "VerkGemLKF",
                        "VerkGemFastigheter",
                        "Vagar",
                        "VFS_PlaneradVag",
                        "Satellitbilder",
                        "Drone",
                        "smittad_zon_sjv",
                        "VerkGemFaktaExternVindkraft",
                        "VerkGemFaktaBelastningar",
                        "destinationer",
                        "mottagningsplatsicons",
                        this.aoDestineraLayerId,
                        this.destineringsomradenId,
                        "destineringsomraden_select"
                    ]
                }
            ],
            initalSubset: "neutral",
            icons: [

                {
                    id: "truck",
                    arcgisStyle: "path",
                    color: "black",
                    size: 12,
                    iconSvgPath: "M624 352h-16V243.9c0-12.7-5.1-24.9-14.1-33.9L494 110.1c-9-9-21.2-14.1-33.9-14.1H416V48c0-26.5-21.5-48-48-48H48C21.5 0 0 21.5 0 48v320c0 26.5 21.5 48 48 48h16c0 53 43 96 96 96s96-43 96-96h128c0 53 43 96 96 96s96-43 96-96h48c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zM160 464c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm320 0c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48zm80-208H416V144h44.1l99.9 99.9V256z"
                },

                {
                    id: "fabrik",
                    arcgisStyle: "path",
                    color: "black",
                    size: 12,
                    outlineWidth: 0.7,
                    outlineColor: [
                        240,
                        240,
                        240
                    ],
                    iconSvgPath: "M475.115 163.781L336 252.309v-68.28c0-18.916-20.931-30.399-36.885-20.248L160 252.309V56c0-13.255-10.745-24-24-24H24C10.745 32 0 42.745 0 56v400c0 13.255 10.745 24 24 24h464c13.255 0 24-10.745 24-24V184.029c0-18.917-20.931-30.399-36.885-20.248z"
                },

                {
                    id: "delad",
                    width: "14px",
                    height: "14px",
                    iconUrlPath: "/core/assets/img/karta/multi.png",
                    iconUrlPathSelected: "/core/assets/img/karta/multi_selected.png"
                },

                {
                    id: "road",
                    width: "14px",
                    height: "14px",
                    xoffset: 5,
                    yoffset: -5,
                    iconUrlPath: "/core/assets/img/karta/vaglager.png",
                    iconUrlPathSelected: "/core/assets/img/karta/vaglager_selected.png"
                },

                {
                    id: "deldestinerad",
                    width: "14px",
                    height: "14px",
                    iconUrlPath: "/core/assets/img/karta/deldestinerad.png",
                    iconUrlPathSelected: "/core/assets/img/karta/deldestinerad_selected.png"
                },

                {
                    id: "delad-diamant",
                    width: "14px",
                    height: "14px",
                    iconUrlPath: "/core/assets/img/karta/multi_befraktare.svg",
                    iconUrlPathSelected: "/core/assets/img/karta/multi_befraktare_selected.svg"
                }
            ]
        };
    }

    layerReady(map: IMapComponent, layer: string) {
        this.map = map;
        if (layer === "transportforetagicons") {
            this.refreshTransportforetagIcons();
        }
        if (layer === "mottagningsplatsicons") {
            this.refreshMottagningsplatsIcons();
        }
        if (layer === "Transportomraden") {
            this.filterTransportomraden();
        } else if (layer === this.aoNeutralLayerId || layer === "arbetsordrar" || layer === this.aoDestineraLayerId) {
            setTimeout(() => {
                this.tplLayerBinder.renderArbetsordrar(this.state.name);
            }, 100);
        }
    }

    layerVisibilityChanged($event: LayerVisibility): void {
        if ($event.layerid === this.destineringsomradenId && $event.visible === false) {
            this.selectedDestineringsomraden.splice(0);
            this.map?.clearGeometries("destineringsomraden_select");
        }
    }

    loadMottagningsplatsIcons() {
        this.transportforetagService.getAllMottagningsplatsMarkers().subscribe(result => {
            this.mottagningsplatsIcons = result;
            this.refreshMottagningsplatsIcons();
        });
    }

    loadTransportforetagIcons() {
        this.transportforetagService.getAllTransportforetag().subscribe(result => {
            this.transportForetag = result;
            this.refreshTransportforetagIcons();
        });
    }

    mapPan(map: any) {
        this.hideTooltip();
    }

    mapReady(map: IMapComponent) {
        this.map = map;
        this.tplLayerBinder = new TplLayerBinder(this.map, this.mapsettings, this.stateService);
        this.tplLayerBinder
            .bind({
                statename: "search",
                layers: [{ layerid: this.aoNeutralLayerId }]
            })
            .bind({
                statename: "filter",
                layers: [{ layerid: this.aoNeutralLayerId }]
            })
            .bind({
                statename: "transporttilldelning",
                layers: [
                    {
                        layerid: "arbetsordrar",
                        colorFunc: ao => this.getAoColor(ao),
                        iconFunc: ao => this.isDeladArbertsorder(ao)
                    }
                ]
            })
            .bind({
                statename: "omdestinera",
                layers: [
                    { layerid: this.aoDestineraLayerId },
                    { layerid: "destinationer" }
                ]
            });
    }

    mottagningsplatsIconClick(geometry: IMapGeometry, mouseevent: any) {
        if (geometry) {
            TplMottagningsplatsModalComponent.open(geometry.id, this.modal);
        }
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        this.initMapSettings();
        this.loadTransportforetagIcons();
        this.loadMottagningsplatsIcons();

        this.stateService.currentState
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(state => {
                this.state = state;
                this.setState(state.name);
            });

        const subscription = this.stateService.init
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(state => {
                if (state) {
                    state.arbetsordrar
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe((ao) => {
                            this.clearFilterDestineringsomraden();
                        });

                    state.currentArbetsordrar
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(filtered => {
                            this.tplLayerBinder?.renderArbetsordrar(this.state.name, true);
                            const ids = filtered?.map(f => f.id);
                            this.tplLayerBinder?.setFilterCurrent(ids);
                            setTimeout(() => {
                                this.ao = ids;
                                this.refreshMottagningsplatsIcons();
                            });
                        });

                    state.modifiedArbetsordrar
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(arbetsordrar => {
                            this.tplLayerBinder?.renderArbetsordrar(this.state.name);
                            this.ao = arbetsordrar?.map(x => x.id);
                            this.refreshMottagningsplatsIcons();
                        });

                    state.selected
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(event => {
                            if (event.origin !== "karta") {
                                this.tplLayerBinder?.setSelectedCurrent();
                            }
                            this.aoSelected = event.selected;
                        });

                    state.transportsatt
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(event => {
                            this.transportsatt = event;
                            this.filterTransportomraden();
                        });

                    state.selectedHs
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(event => {
                            this.tplLayerBinder?.renderCurrent();
                            this.ao = [].concat(this.ao);
                        });

                    state.selectedMottagningsplatser
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(event => {
                            this.tplLayerBinder?.renderCurrent();
                        });

                    state.destineringHs
                        .pipe(takeUntil(this.ngUnsubscribe$))
                        .subscribe(event => {
                            this.tplLayerBinder?.renderCurrent();
                        });

                    subscription?.unsubscribe();
                }
            });

        // selected in map
        this.eventService.subscribeEvent<MapSelectedEvent>(MapSelectedEvent.EVENTNAME)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.stateService.current.addSelected({
                    selected: event.selected.map(g => g.id),
                    origin: "karta"
                });
                setTimeout(() => {
                    this.triggerDigestCycle();
                }, 100);
            });

        this.eventService.subscribeEvent<MapDeselectedEvent>(MapDeselectedEvent.EVENTNAME)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.stateService.current.removeSelected({
                    selected: event.deselected.map(g => g.id),
                    origin: "karta"
                });
            });

        // hover
        this.eventService
            .subscribeEvent<MapHoverEvent>("MapHoverEvent")
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe();

        this.eventService.subscribeEvent<ArbetsorderListHoverInMessage>(ArbetsorderListHoverInMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                const subset = this.map?.getSubset();
                if (subset === "neutral") {
                    const geom = this.mapsettings.drawLayers.find(p => p.id === this.aoNeutralLayerId)
                        .geometries.find(p => p.id === event.arbetsorderId);
                    this.map.highlightGeometries(this.aoNeutralLayerId, [geom]);
                } else if (subset === "omdestinera") {
                    const geom = this.mapsettings.drawLayers.find(p => p.id === this.aoDestineraLayerId)
                        .geometries.find(p => p.id === event.arbetsorderId);
                    this.map.highlightGeometries(this.aoDestineraLayerId, [geom]);
                } else if (subset === "transporttilldelning") {
                    const geom = this.mapsettings.drawLayers.find(p => p.id === "arbetsordrar")
                        .geometries.find(p => p.id === event.arbetsorderId);
                    this.map.highlightGeometries("arbetsordrar", [geom]);
                }
            });

        this.eventService.subscribeEvent<ArbetsorderListHoverOutMessage>(ArbetsorderListHoverOutMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.map?.clearAllHighlights();
            });

        this.eventService
            .subscribeEvent<TplFilterApplied>(TplFilterApplied.type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.filterFeatureLayers(event);
            });

        this.eventService
            .subscribeEvent<TplFilterReset>(TplFilterReset.type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                this.clearFilterDestineringsomraden();
            });
    }

    onMapClick(mouse, hitlist) {
        if (this.map.isWidgetsActive(["map-layers"])) {
            this.map.closeWidgets();
        }
    }

    refreshMottagningsplatsIcons() {
        if (this.mottagningsplatsIcons) {
            const applicableMottagningsplatser = this.tplService.resolveApplicableMottagningsplatser(this.stateService.current.getCurrentArbetsordrar());

            const isApplicableMottagningsplats = (mpl: MottagningsplatsMarkerDTO): boolean => applicableMottagningsplatser.some(x => x.mottagningsplatsId === mpl.id);

            const layer = this.mapsettings.drawLayers.find(x => x.id === "mottagningsplatsicons");
            layer.geometries = this.mottagningsplatsIcons
                .filter(isApplicableMottagningsplats)
                .map(dto => (({
                    id: dto.id,
                    iconid: "fabrik",
                    visible: true,
                    easting: dto.easting,
                    northing: dto.northing,
                    tag: dto,
                    label: dto.namn
                })));
            this.refresh.next({ layerId: layer.id });
        }
    }

    refreshTransportforetagIcons() {
        if (this.transportForetag) {
            // eslint-disable-next-line no-bitwise
            const omradeFlaggor = (this.transportsatt.rundvirke ? 1 : 0) | (this.transportsatt.bransle ? 2 : 0);
            const foretagGeometries = this.transportforetagService.getGeometriesForTransportforetag(this.transportForetag, omradeFlaggor);
            const layer = this.mapsettings.drawLayers.find(x => x.id === "transportforetagicons");
            layer.geometries = foretagGeometries;
            this.refresh.next({ layerId: layer.id });
        }
    }

    showArbetsorderLegend() {
        return this.stateService.current.showArbetsorderLegend;
    }

    showTooltip(text: string, x: number, y: number, id?: number, route?: string) {
        const element = this.popup.nativeElement;
        const modulo = 32;
        this.renderer.setStyle(element, "top", `${y - modulo}px`);
        this.renderer.setStyle(element, "left", `${x}px`);
        this.clickedItem.label = text;
        this.clickedItem.route = route;
        this.clickedItem.id = id;
        this.clickedItem.visible = true;
        this.triggerDigestCycle();
    }

    transportforetagIconClick(geometry: IMapGeometry, mouseevent: any) {
        if (geometry) {
            this.showTooltip(geometry?.label, mouseevent.x, mouseevent.y, geometry?.id, "transportforetag");
        }
    }

    updateSelectedDestineringsomrade(omrade: IMapGeometry, isCtrl: boolean) {
        const isSelectedBefore = this.selectedDestineringsomraden.find(x => x.id === omrade.id);
        if (isCtrl === false) {
            this.clearAllSelectedDestineringsomrade();
            this.addSelectedDestineringsomrade(omrade);
        } else {
            if (isSelectedBefore) {
                this.removeSelectedDestineringsomrade(omrade);
            } else {
                this.addSelectedDestineringsomrade(omrade);
            }
        }
    }

    private addSelectedDestineringsomrade(omrade: IMapGeometry) {
        this.selectedDestineringsomraden.push(omrade);
        this.map?.setGeometries("destineringsomraden_select", this.selectedDestineringsomraden);

        const addingGeometries = this.map.getGeometriesByGeometry(this.aoDestineraLayerId, omrade);
        const selected = this.stateService.current.getSelected().selected;

        addingGeometries.forEach(g => {
            selected.push(g.id);
        });
        this.tplLayerBinder.setSelected(selected);
    }

    private clearAllSelectedDestineringsomrade() {
        this.selectedDestineringsomraden.splice(0);
        this.map?.clearSelection();
        this.map?.clearGeometries("destineringsomraden_select");
    }

    private clearFilterDestineringsomraden() {
        this.filterDestineringsomraden(null);
        this.filteredDestineringsomraden = [];
        this.selectedDestineringsomraden.splice(0);
        this.map?.setGeometries("destineringsomraden_select", this.selectedDestineringsomraden);
    }

    private filterDestineringsomraden(expression: string) {
        this.map?.filterLayer(this.destineringsomradenId, expression, ["Destineringsområdeytor"]);
    }

    private filterFeatureLayers(event: TplFilterApplied) {
        if (event.filterState?.destineringsomrade?.length > 0 || event.filterState?.mottagningsplats?.length > 0 || event.filterState?.handelssortiment?.length > 0) {
            const mplid = uniq(event.filterState?.mottagningsplats.map(x => parseInt(x, 10)));
            const query = {
                mottagningsplatser: mplid,
                handelssortiment: event.filterState?.handelssortiment
            };
            this.tplService.loadLinkedDestineringsomraden(query).subscribe(dsoIdList => {
                this.filteredDestineringsomraden = uniq([
                    ...event.filterState.destineringsomrade,
                    ...dsoIdList.destineringsomraden
                ]);
                this.setFilterDestineringsomraden(this.filteredDestineringsomraden);
            });
        } else {
            this.clearFilterDestineringsomraden();
        }
    }

    private filterTransportomraden() {
        const filter = [];
        if (this.transportsatt.rundvirke) {
            filter.push("Transportsatt = 'Rundvirke'");
        }
        if (this.transportsatt.bransle) {
            filter.push("Transportsatt = 'Bransle'");
        }
        const filterExpression = `${filter.join(" OR ")}`;
        setTimeout(() => {
            this.filter.next({
                layerId: "Transportomraden",
                sublayerId: "Transportområdeytor",
                filterExpression: filterExpression
            });
        }, 200);
    }

    private getAoColor(ao: ITplArbetsorder): string {
        if (ao.handelssortiment.some(h => !!h.befraktareColor)) {
            return ao.handelssortiment.filter(h => !!h.befraktareColor)[0].befraktareColor;
        } else if (ao.color) {
            return ao.color;
        }
        return "white";
    }

    private handleTplArbetsorderInfoModalResult(geometry: IMapGeometry, result: ICallbackResult): void {
        if (geometry.selected === result.isSelected) {
            return;
        }

        if (result.isSelected) {
            this.aoSelected.push(geometry.id);
        } else {
            const idx = this.aoSelected.findIndex(x => x === geometry.id);
            if (idx > -1) {
                this.aoSelected.splice(idx, 1);
            }
        }

        this.stateService.current.setSelected({
            selected: this.aoSelected,
            origin: "karta"
        });
        this.tplLayerBinder?.setSelectedCurrent();
    }

    private isDeladArbertsorder(ao: ITplArbetsorder): string {
        if (this.tplService.aoHasBefraktareAndAreDifferent(ao)) {
            return "delad-diamant";
        } else if (ao.handelssortiment.some(x => !!x.transportforetagId)) {
            return "delad";
        }
        return null;
    }

    private removeSelectedDestineringsomrade(omrade: IMapGeometry) {
        remove(this.selectedDestineringsomraden, dso => dso.id === omrade.id);
        this.map?.setGeometries("destineringsomraden_select", this.selectedDestineringsomraden);

        const removingGeometries = this.map.getGeometriesByGeometry(this.aoDestineraLayerId, omrade);
        const selected = this.stateService.current.getSelected().selected;
        remove(selected, sel => removingGeometries.some(g => g.id === sel));
        this.tplLayerBinder.setSelected(selected);
    }

    private setFilterDestineringsomraden(omraden: any[]) {
        if (omraden?.length > 0) {
            const filterGUIDS = omraden.map(omrade => `Id = ${omrade}`);
            const filter = filterGUIDS.join(" OR ");
            this.filterDestineringsomraden(filter);
        } else {
            this.filterDestineringsomraden("Id = -1");
        }
    }

    private setState(statename: string) {
        this.targetState = statename;
        this.switchState();
    }

    private switchState() {
        if (ObjectUtils.isNullOrUndefined(this.changingState)) {
            this.changingState = window.setInterval(() => {
                if (this.map?.allLayersLoaded) {
                    clearInterval(this.changingState);
                    this.changingState = null;
                    const neutralStates = [
                        TplTabId.search,
                        TplTabId.filter,
                        TplTabId.handelser,
                        TplTabId.hanteraao
                    ];
                    this.map?.setSubset(neutralStates.includes(this.targetState) ? "neutral" : this.targetState);
                }
            }, 50);
        }
    }

    private triggerDigestCycle(): void {
        this.changeDetectorRef.detectChanges();
    }
}
